import React, { useState, useMemo, useEffect } from "react";
import {
  Flex,
  Input,
  Select,
  Typography,
  Image,
  Upload,
  Button,
  message,
  Checkbox,
  Collapse,
  AutoComplete,
} from "antd";
import { WsProduct } from "../../../api/webshop/wsProductsApi";
import { DefaultOptionType } from "antd/es/select";
import { BASE_URL } from "../../../api/emptyV2Api";
import handleUploadFile from "../../../utils/uploadFileS3";
import AssetPopup from "./assetPopup";
import { buildWsProductSiteLink } from "./exportWsProducts";

const { Text, Link } = Typography;

const statusOptions: DefaultOptionType[] = [
  { label: "ACTIVE", value: "ACTIVE" },
  { label: "INACTIVE", value: "INACTIVE" },
];

const promotionTagOptions: DefaultOptionType[] = [
  { label: "BEST SELLER", value: "BEST SELLER" },
  { label: "RECOMMENDED", value: "RECOMMENDED" },
  { label: "CHEAPEST", value: "CHEAPEST" },
  { label: "AFFORDABLE", value: "AFFORDABLE" },
  { label: "UNLIMITED DATA", value: "UNLIMITED DATA" },
];

const predefinedRegions = [
  "APAC",
  "ASIA",
  "EUROPE",
  "LAM",
  "SEA",
  "SOUTH ASIA",
  "JAPAC",
];

export default function ProductInformationTab(props: {
  p: WsProduct;
  setP: (p: WsProduct) => void;
}) {
  const dev_assets_url =
    "https://webshop-assets-20260531210740877800000001.s3.amazonaws.com/products";
  const prod_assets_url =
    "https://webshop-assets-20260531232150401200000002.s3.amazonaws.com/products";
  const ASSETS_ENDPOINT =
    process.env.NODE_ENV === "production" ||
    process.env.VERCEL_ENV === "production"
      ? prod_assets_url
      : dev_assets_url;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [customFilename, setCustomFilename] = useState("");
  const [fileExtension, setFileExtension] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [assetPopupOpen, setAssetPopupOpen] = useState(false);
  const [assetPopupType, setAssetPopupType] = useState<
    "products/" | "network/"
  >("products/");
  const [cachedDataTag, setCachedDataTag] = useState<{
    label: string;
    value: string;
  }>(props.p.productPromotions?.dataTag || { label: "", value: "" });
  const [cachedPriceTag, setCachedPriceTag] = useState<{
    value: string;
    unit: "PERCENT" | "FIXED" | "";
  }>(props.p.productPromotions?.priceTag || { value: "", unit: "PERCENT" });

  useEffect(() => {
    if (typeof props.p.productPromotions === "string") {
      try {
        const parsed = JSON.parse(props.p.productPromotions);
        props.setP({
          ...props.p,
          productPromotions: parsed,
        });
      } catch (err) {
        console.error("Failed to parse productPromotions JSON string:", err);
      }
    }
  }, [props.p.productPromotions, props.p, props.setP]);

  useEffect(() => {
    if (props.p.productPromotions?.dataTag)
      setCachedDataTag(props.p.productPromotions.dataTag);
    if (props.p.productPromotions?.priceTag)
      setCachedPriceTag(props.p.productPromotions.priceTag);
  }, [props.p.productPromotions]);

  // Tags are now directly edited via input/autocomplete.

  const selectedCountries = props.p.coverageCountries || [];

  const toggleCountry = (country: string, checked: boolean) => {
    const updated = checked
      ? [...selectedCountries, country]
      : selectedCountries.filter((c) => c !== country);
    props.setP({ ...props.p, coverageCountries: updated });
  };

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return allCountries;
    return allCountries.filter((c) =>
      c.toLowerCase().includes(countrySearch.toLowerCase()),
    );
  }, [countrySearch]);

  const handleSelectAll = () => {
    const toAdd = filteredCountries.filter(
      (c) => !selectedCountries.includes(c),
    );
    props.setP({
      ...props.p,
      coverageCountries: [...selectedCountries, ...toAdd],
    });
  };

  const handleDeselectAll = () => {
    const toRemove = new Set(filteredCountries);
    props.setP({
      ...props.p,
      coverageCountries: selectedCountries.filter((c) => !toRemove.has(c)),
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      const newName = fileExtension
        ? `${customFilename.trim()}.${fileExtension}`
        : customFilename.trim();

      const renamedFile = new File([selectedFile], newName, {
        type: selectedFile.type,
      });
      const uploadUrl = `${BASE_URL}webshop/products/manage?action=webshop-assets`;

      const result = await handleUploadFile(uploadUrl, renamedFile);
      if (result) {
        message.success("Image uploaded successfully!");
        props.setP({ ...props.p, imageUrl: result });
        setSelectedFile(null);
        setCustomFilename("");
        setFileExtension("");
      } else {
        message.error("Failed to upload image.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      message.error("An error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {assetPopupOpen && (
        <AssetPopup
          open={assetPopupOpen}
          onClose={() => setAssetPopupOpen(false)}
          onSelect={(filename: string) => {
            if (assetPopupType === "network/") {
              props.setP({ ...props.p, network: filename });
            } else {
              props.setP({ ...props.p, imageUrl: filename });
            }
            setAssetPopupOpen(false);
          }}
          prefix={assetPopupType}
        />
      )}
      <Flex vertical gap={12} style={{ padding: "12px 0" }}>
        <Flex gap={24}>
          <Input
            addonBefore="Product ID"
            disabled
            value={props.p.productId || "(Auto-generated)"}
          />
          <Button
            type="primary"
            onClick={() => {
              if (
                props.p.coverageCountries &&
                props.p.coverageCountries.length > 0 &&
                props.p.productId
              ) {
                window.open(
                  buildWsProductSiteLink(
                   props.p.productLink || props.p.productId,
                  ),
                  "_blank",
                );
              } else {
                message.warning("No coverage country set for this product");
              }
            }}
          >
            View on Website
          </Button>
        </Flex>
        <Flex gap={12} align="center">
          <Input
            addonBefore="Product Title"
            value={props.p.title}
            onChange={(e) => props.setP({ ...props.p, title: e.target.value })}
            placeholder="e.g. Thailand - Daily Plan"
            style={{ flex: 2 }}
          />

          <Flex gap={8} align="center" justify="end" style={{ flex: 1 }}>
            <Text style={{ whiteSpace: "nowrap" }}>Status</Text>
            <Select
              options={statusOptions}
              value={props.p.status || "ACTIVE"}
              onChange={(v) => props.setP({ ...props.p, status: v })}
              style={{ minWidth: 120, flex: 1 }}
            />
          </Flex>
        </Flex>

        <Input
          addonBefore="Product Name"
          value={props.p.productName}
          onChange={(e) =>
            props.setP({ ...props.p, productName: e.target.value })
          }
          placeholder="e.g. Simply 30gb data plan"
          style={{ flex: 2 }}
        />
        <Input
          addonBefore="Product Link"
          value={props.p.productLink}
          onChange={(e) =>
            props.setP({ ...props.p, productLink: e.target.value })
          }
          placeholder="e.g. apac-30gb-data-plan"
          style={{ flex: 2 }}
        />

        <Input
          addonBefore="Product Tags"
          value={props.p.productTags}
          onChange={(e) =>
            props.setP({ ...props.p, productTags: e.target.value })
          }
          placeholder="e.g. tag1, tag2"
        />

        <Flex
          vertical
          gap={8}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: "8px",
            padding: "16px",
            background: "#ffffff",
          }}
        >
          <Text strong>Product Promotions</Text>
          <Flex vertical gap={16}>
            {/* Row 1: Tag */}
            <Flex gap={8} align="center">
              <Text style={{ whiteSpace: "nowrap" }}>Promotion Tag</Text>
              <AutoComplete
                options={promotionTagOptions}
                allowClear
                placeholder="Type or select a tag"
                value={props.p.productPromotions?.tag || ""}
                onChange={(val) => {
                  props.setP({
                    ...props.p,
                    productPromotions: {
                      ...props.p.productPromotions,
                      tag: val,
                    },
                  });
                }}
                style={{ width: 250 }}
              />
            </Flex>

            {/* Row 2: Data Tag & Price Tag */}
            <Flex gap={16}>
              <Flex vertical gap={8} style={{ flex: 1 }}>
                <Checkbox
                  checked={!!props.p.productPromotions?.dataTag}
                  onChange={(e) => {
                    if (e.target.checked) {
                      props.setP({
                        ...props.p,
                        productPromotions: {
                          ...props.p.productPromotions,
                          dataTag: cachedDataTag,
                        },
                      });
                    } else {
                      const { dataTag, ...rest } =
                        props.p.productPromotions || {};
                      props.setP({
                        ...props.p,
                        productPromotions: Object.keys(rest).length
                          ? (rest as any)
                          : undefined,
                      });
                    }
                  }}
                >
                  Enable Data Bonus
                </Checkbox>
                {props.p.productPromotions?.dataTag && (
                  <Flex vertical gap={8}>
                    <Text strong>Data Promotion Details</Text>
                    <Flex gap={8}>
                      <Input
                        placeholder="Label (e.g. get 3 gb free data)"
                        value={props.p.productPromotions.dataTag.label}
                        onChange={(e) => {
                          props.setP({
                            ...props.p,
                            productPromotions: {
                              ...props.p.productPromotions,
                              dataTag: {
                                ...props.p.productPromotions!.dataTag!,
                                label: e.target.value,
                              },
                            },
                          });
                        }}
                        style={{ flex: 1 }}
                      />
                      {/* <Input
                        placeholder="Data Value (e.g. 3)"
                        value={props.p.productPromotions.dataTag.value}
                        onChange={(e) => {
                          props.setP({
                            ...props.p,
                            productPromotions: {
                              ...props.p.productPromotions,
                              dataTag: {
                                ...props.p.productPromotions!.dataTag!,
                                value: e.target.value,
                              },
                            },
                          });
                        }}
                        style={{ flex: 1 }}
                      /> */}
                    </Flex>
                  </Flex>
                )}
              </Flex>

              <Flex vertical gap={8} style={{ flex: 1 }}>
                <Checkbox
                  checked={!!props.p.productPromotions?.priceTag}
                  onChange={(e) => {
                    if (e.target.checked) {
                      props.setP({
                        ...props.p,
                        productPromotions: {
                          ...props.p.productPromotions,
                          priceTag: cachedPriceTag,
                        },
                      });
                    } else {
                      const { priceTag, ...rest } =
                        props.p.productPromotions || {};
                      props.setP({
                        ...props.p,
                        productPromotions: Object.keys(rest).length
                          ? (rest as any)
                          : undefined,
                      });
                    }
                  }}
                >
                  Enable Price Discount
                </Checkbox>
                {props.p.productPromotions?.priceTag && (
                  <Flex vertical gap={8}>
                    <Text strong>Price Promotion Details</Text>
                    <Flex gap={8}>
                      <Input
                        type="number"
                        placeholder="Value"
                        value={props.p.productPromotions.priceTag.value}
                        onChange={(e) => {
                          props.setP({
                            ...props.p,
                            productPromotions: {
                              ...props.p.productPromotions,
                              priceTag: {
                                ...props.p.productPromotions!.priceTag!,
                                value: e.target.value,
                              },
                            },
                          });
                        }}
                        style={{ flex: 2 }}
                      />
                      <Select
                        value={
                          props.p.productPromotions.priceTag.unit || "PERCENT"
                        }
                        onChange={(val) => {
                          props.setP({
                            ...props.p,
                            productPromotions: {
                              ...props.p.productPromotions,
                              priceTag: {
                                ...props.p.productPromotions!.priceTag!,
                                unit: val as "PERCENT" | "FIXED",
                              },
                            },
                          });
                        }}
                        options={[
                          { label: "%", value: "PERCENT" },
                          { label: "Fixed", value: "FIXED" },
                        ]}
                        style={{ flex: 1 }}
                      />
                    </Flex>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Collapse
          size="small"
          items={[
            {
              key: "regions",
              label: (
                <Flex align="center" gap={8}>
                  <Text strong>Coverage Regions</Text>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    ({(props.p.regions || []).length} selected)
                  </Text>
                </Flex>
              ),
              children: (
                <Flex vertical gap={12}>
                  <Flex wrap="wrap" gap={8}>
                    {predefinedRegions.map((region) => (
                      <Checkbox
                        key={region}
                        checked={(props.p.regions || []).includes(region)}
                        onChange={(e) => {
                          const currentRegions = props.p.regions || [];
                          const updated = e.target.checked
                            ? [...currentRegions, region]
                            : currentRegions.filter((r) => r !== region);
                          props.setP({ ...props.p, regions: updated });
                        }}
                      >
                        {region}
                      </Checkbox>
                    ))}
                  </Flex>
                  <Select
                    mode="tags"
                    style={{ width: "100%" }}
                    placeholder="Type custom region and press enter or comma"
                    tokenSeparators={[","]}
                    value={props.p.regions || []}
                    onChange={(val: string[]) => {
                      const upperVal = val.map((v) => v.toUpperCase());
                      props.setP({
                        ...props.p,
                        regions: Array.from(new Set(upperVal)),
                      });
                    }}
                    options={predefinedRegions.map((r) => ({
                      label: r,
                      value: r,
                    }))}
                  />
                </Flex>
              ),
            },
          ]}
        />
        <Collapse
          size="small"
          items={[
            {
              key: "coverage",
              label: (
                <Flex align="center" gap={8}>
                  <Text strong>Coverage Countries</Text>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    ({selectedCountries.length} selected)
                  </Text>
                </Flex>
              ),
              children: (
                <Flex vertical gap={8}>
                  <Flex gap={8} align="center">
                    <Input
                      placeholder="Search countries..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      allowClear
                      style={{ flex: 1 }}
                    />
                    <Button size="small" onClick={handleSelectAll}>
                      Select All
                    </Button>
                    <Button size="small" onClick={handleDeselectAll}>
                      Deselect All
                    </Button>
                  </Flex>
                  <div
                    style={{
                      maxHeight: "260px",
                      overflowY: "auto",
                      border: "1px solid #f0f0f0",
                      borderRadius: "6px",
                      padding: "8px 12px",
                    }}
                  >
                    <Flex wrap="wrap" gap={4}>
                      {filteredCountries.map((country) => {
                        const isChecked = selectedCountries.includes(country);
                        return (
                          <Checkbox
                            key={country}
                            checked={isChecked}
                            onChange={(e) =>
                              toggleCountry(country, e.target.checked)
                            }
                            style={{
                              width: "calc(33.33% - 4px)",
                              minWidth: "180px",
                              color: isChecked ? "inherit" : "#bfbfbf",
                              marginInlineStart: 0,
                            }}
                          >
                            {country}
                          </Checkbox>
                        );
                      })}
                    </Flex>
                  </div>
                </Flex>
              ),
            },
          ]}
        />
        <Input.TextArea
          placeholder="Enter Default Product Description..."
          value={props.p.description}
          onChange={(e) =>
            props.setP({ ...props.p, description: e.target.value })
          }
          rows={4}
          style={{ borderRadius: "6px" }}
        />
        <Flex gap={4} align="center">
          <Input
            addonBefore="Network Name"
            value={props.p.network}
            onChange={(e) =>
              props.setP({ ...props.p, network: e.target.value })
            }
            onClick={() => {
              setAssetPopupType("network/");
              setAssetPopupOpen(true);
            }}
            placeholder="e.g. AIS / DTAC"
            style={{ flex: 1 }}
          />
          <Input
            addonBefore="Image URL / Tag"
            value={props.p.imageUrl}
            onChange={(e) =>
              props.setP({ ...props.p, imageUrl: e.target.value })
            }
            onClick={() => {
              setAssetPopupType("products/");
              setAssetPopupOpen(true);
            }}
            placeholder="e.g. Thailand"
            style={{ flex: 1 }}
          />
          <Upload
            beforeUpload={(file) => {
              const isJpgOrPng =
                file.type === "image/jpeg" ||
                file.type === "image/png" ||
                file.type === "image/webp";
              if (!isJpgOrPng) {
                message.error("You can only upload JPG/PNG/WEBP/GIF files!");
                return false;
              }
              setSelectedFile(file);
              const lastDotIndex = file.name.lastIndexOf(".");
              let baseName = file.name;
              let ext = "";
              if (lastDotIndex !== -1) {
                baseName = file.name.substring(0, lastDotIndex);
                ext = file.name.substring(lastDotIndex + 1);
              }
              setCustomFilename(baseName);
              setFileExtension(ext);
              return false; // prevent auto-upload
            }}
            showUploadList={false}
            accept="image/*"
          >
            <Button type="primary" disabled={isUploading}>
              Select Image
            </Button>
          </Upload>
        </Flex>

        {selectedFile && (
          <Flex
            vertical
            gap={8}
            style={{
              border: "1px dashed #d9d9d9",
              padding: "12px",
              borderRadius: "8px",
              background: "#fafafa",
            }}
          >
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Selected File: <Text code>{selectedFile.name}</Text>
            </Text>
            <Flex gap={8} align="center">
              <Input
                addonBefore="Rename File"
                addonAfter={fileExtension ? `.${fileExtension}` : undefined}
                placeholder="Enter custom file name..."
                value={customFilename}
                onChange={(e) => setCustomFilename(e.target.value)}
                disabled={isUploading}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                onClick={handleUpload}
                disabled={isUploading || !customFilename.trim()}
                loading={isUploading}
              >
                Upload
              </Button>
              <Button
                onClick={() => {
                  setSelectedFile(null);
                  setCustomFilename("");
                  setFileExtension("");
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
            </Flex>
          </Flex>
        )}

        <Flex
          gap={16}
          align="start"
          justify="space-between"
          style={{ marginTop: "16px" }}
        >
          {/* Network Image Section */}
          <Flex vertical gap={4} align="center" style={{ flex: 1 }}>
            {props.p.network && (
              <>
                <Text strong>Network Image</Text>
                <Image
                  width={200}
                  height={120}
                  src={`${ASSETS_ENDPOINT.replace("/products", "/network")}/${props.p.network}`}
                  fallback="https://placehold.co/200x120?text=Network+Image"
                  style={{
                    borderRadius: 8,
                    objectFit: "cover",
                    border: "1px solid #f0f0f0",
                  }}
                />
                <Link
                  href={`${ASSETS_ENDPOINT.replace("/products", "/network")}/${props.p.network}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "12px", marginTop: "4px" }}
                >
                  Open image in new tab
                </Link>
              </>
            )}
          </Flex>

          {/* Product Image Section */}
          <Flex vertical gap={4} align="center" style={{ flex: 1 }}>
            {props.p.imageUrl && (
              <>
                <Text strong>Product Image</Text>
                <Image
                  width={200}
                  height={120}
                  src={`${ASSETS_ENDPOINT}/${props.p.imageUrl}`}
                  fallback="dataTag:image/png;base64,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"
                  style={{
                    borderRadius: 8,
                    objectFit: "cover",
                    border: "1px solid #f0f0f0",
                  }}
                />
                <Link
                  href={`${ASSETS_ENDPOINT}/${props.p.imageUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "12px", marginTop: "4px" }}
                >
                  Open image in new tab
                </Link>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}

const allCountries = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo (Congo-Brazzaville)",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czech Republic",
  "Democratic Republic of the Congo",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Ivory Coast",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine State",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
];
